import { faGlobe, faServer, faTrophy } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { useUser } from '~/features/auth/api/useUser'
import { useKillRaceInfo } from '../api/useKillRaceInfo'
import { useKillRaceLeaderboard } from '../api/useKillRaceLeaderboard'
import { TournamentEntry as Entry } from '../types'
import { formatDateTime, formatUsd, toNumber } from '../utils/killrace'
import { ErrorBlock, Panel, PRIZE_GREEN, Skeleton } from './KillRaceUI'

/**
 * Left rail of the race page: the money, then the viewer's own standing, then
 * the facts. Ordered that way deliberately — "what can I win" and "where am I"
 * are the two questions this page exists to answer.
 */
export const KillRaceInfo = ({ uuid }: { uuid: string }) => {
    const { data, isLoading, isError, refetch } = useKillRaceInfo(uuid)
    const { data: board } = useKillRaceLeaderboard(uuid)
    const { data: user } = useUser()

    if (isLoading) {
        return (
            <Panel className='flex flex-col gap-4 p-6'>
                <Skeleton className='h-16 w-full' />
                <Skeleton className='h-24 w-full' />
                <Skeleton className='h-40 w-full' />
            </Panel>
        )
    }

    if (isError) {
        return (
            <ErrorBlock title="Couldn't load this race" onRetry={refetch}>
                The race may have been removed, or the link may be wrong.
            </ErrorBlock>
        )
    }

    const entries = Array.isArray(board) ? board : []
    const servers: string[] = data?.servers ?? []

    // The standings are capped at the paid places, so the viewer's row is only
    // in `entries` when they placed. `user_entry` is the same row from the
    // detail endpoint and is there either way, which is what lets this panel
    // answer "where am I" for a player who entered and did not place, instead
    // of showing a dash and telling them they are not on the board.
    const mine: Entry | undefined = entries.find((entry) => entry.user_id === user?.id) ?? data?.user_entry

    // Both fields answer "what would I win if it ended now" and now agree: both
    // are derived from a rank taken across the whole field.
    const potential = toNumber(mine ? mine.potential_reward : data?.user_live_earnings)

    return (
        <Panel className='flex flex-col'>
            <div className='flex flex-col items-center gap-2 border-b border-white/10 px-6 py-8'>
                <span className='text-2xs uppercase tracking-[0.25em] text-white/40'>Prize pool</span>
                <span
                    className='text-4xl font-black leading-none tabular-nums'
                    style={{ color: PRIZE_GREEN, textShadow: '0 0 28px rgba(0,255,144,0.3)' }}
                >
                    {formatUsd(data?.prize)}
                </span>
                <span className='flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-white/60'>
                    <FontAwesomeIcon icon={faTrophy} className='text-2xs text-white/40' />
                    Top {data?.ladder_size ?? '—'} paid
                </span>
            </div>

            <div className='flex flex-col gap-4 border-b border-white/10 px-6 py-6'>
                <span className='text-2xs uppercase tracking-[0.25em] text-white/40'>Your standing</span>
                <div className='flex items-end justify-between gap-4'>
                    <div className='flex flex-col gap-1'>
                        <span className='text-2xs uppercase tracking-[0.2em] text-white/35'>Rank</span>
                        <span className='text-2xl font-black leading-none tabular-nums'>
                            {mine ? `#${mine.rank}` : '—'}
                        </span>
                    </div>
                    <div className='flex flex-col items-end gap-1'>
                        <span className='text-2xs uppercase tracking-[0.2em] text-white/35'>If it ended now</span>
                        <span
                            className={twMerge(
                                'text-2xl font-black leading-none tabular-nums',
                                potential > 0 ? 'text-[#00FF90]' : 'text-white/30'
                            )}
                        >
                            {formatUsd(potential)}
                        </span>
                    </div>
                </div>
                {/* Now says what it means: no entry at all. It used to fire on
                    "not in the top ten", which is a different thing. */}
                {!mine && (
                    <p className='text-xs font-medium leading-relaxed text-white/40'>
                        You have not entered this race. One kill while it is live puts you on the board.
                    </p>
                )}
            </div>

            <div className='flex flex-col'>
                <InfoRow label='Starts' value={formatDateTime(data?.begin)} />
                <InfoRow label='Ends' value={formatDateTime(data?.end)} />
                <InfoRow
                    label='Mode'
                    value={
                        <span className='flex items-center justify-end gap-2'>
                            <FontAwesomeIcon
                                icon={servers.length > 0 ? faServer : faGlobe}
                                className='text-2xs text-white/40'
                            />
                            {data?.mode ?? 'Global'}
                        </span>
                    }
                />
                {/* The standings endpoint returns at most `ladder_size` rows,
                    so this counts who is on the board, not who entered. */}
                <InfoRow label='On the board' value={entries.length} />
            </div>

            {servers.length > 0 && (
                <div className='flex flex-col gap-2 border-t border-white/10 px-6 py-4'>
                    <span className='text-2xs uppercase tracking-[0.25em] text-white/40'>Servers</span>
                    <ul className='flex flex-col gap-1'>
                        {servers.map((server) => (
                            <li key={server} className='truncate text-sm font-bold text-white/70'>
                                {server}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Panel>
    )
}

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
    <div className='flex items-center justify-between gap-4 border-t border-white/[0.07] px-6 py-3'>
        <span className='text-2xs uppercase tracking-[0.2em] text-white/40'>{label}</span>
        <span className='truncate text-right text-sm font-bold uppercase tabular-nums'>{value}</span>
    </div>
)
